import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import {
  EmailSupportService,
  ISupportEmailDraft,
} from 'src/app/shared/services/email-support.service';

type OpenMenu =
  | null
  | 'fontSize'
  | 'align'
  | 'spacing'
  | 'emoji'
  | 'more'
  | 'send';

@Component({
  selector: 'app-email-support-modal',
  templateUrl: './email-support-modal.component.html',
  styleUrls: ['./email-support-modal.component.scss'],
})
export class EmailSupportModalComponent implements OnInit, AfterViewInit {
  @ViewChild('bodyEditor') bodyEditor!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;

  draft!: ISupportEmailDraft;
  to = '';
  subject = '';
  showFormatToolbar = true;
  selectedFont = 'Sans Serif';
  selectedFontSize = 'Normal';
  selectedAlign = 'left';
  selectedSpacing = '1.15';
  selectedTextColor = '#000000';
  isConfidential = false;
  isExpanded = false;
  openMenu: OpenMenu = null;
  attachmentNames: string[] = [];

  fonts = ['Sans Serif', 'Serif', 'Fixed Width', 'Wide', 'Narrow'];
  fontSizes = [
    { label: 'Small', value: '2' },
    { label: 'Normal', value: '3' },
    { label: 'Large', value: '5' },
    { label: 'Huge', value: '7' },
  ];
  alignments = [
    { label: 'Left', value: 'justifyLeft', icon: 'format_align_left' },
    { label: 'Center', value: 'justifyCenter', icon: 'format_align_center' },
    { label: 'Right', value: 'justifyRight', icon: 'format_align_right' },
    { label: 'Justify', value: 'justifyFull', icon: 'format_align_justify' },
  ];
  spacings = [
    { label: 'Single', value: '1' },
    { label: '1.15', value: '1.15' },
    { label: '1.5', value: '1.5' },
    { label: 'Double', value: '2' },
  ];
  emojis = [
    '😀',
    '🙂',
    '😊',
    '👍',
    '👎',
    '🙏',
    '✅',
    '❌',
    '⚠️',
    '📎',
    '📧',
    '🔒',
    '⭐',
    '💡',
    '📝',
  ];

  constructor(
    public dialogRef: MatDialogRef<EmailSupportModalComponent>,
    private localizationService: LocalizationService,
    private emailSupportService: EmailSupportService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.draft = this.emailSupportService.buildDraft();
    this.to = this.draft.to;
    this.subject = this.draft.subject;
  }

  ngAfterViewInit(): void {
    if (!this.bodyEditor?.nativeElement) {
      return;
    }
    const issueLabel =
      this.translate.instant('EMAIL_SUPPORT_LABEL_ISSUE_DESCRIPTION') + ':';
    const html = this.draft.body
      .split('\n')
      .map((line) => {
        if (line === issueLabel || line.startsWith(issueLabel)) {
          return `<div><strong>${issueLabel}</strong></div>`;
        }
        return line ? `<div>${line}</div>` : '<div><br></div>';
      })
      .join('');
    this.bodyEditor.nativeElement.innerHTML = html;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openMenu = null;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.dialogRef.addPanelClass('email-support-modal-fullscreen');
    } else {
      this.dialogRef.removePanelClass('email-support-modal-fullscreen');
    }
  }

  sendEmail(): void {
    this.openMenu = null;
    let subject = this.subject;
    if (this.isConfidential && !subject.startsWith('[Confidential]')) {
      subject = `[Confidential] ${subject}`;
    }
    const body = this.buildPlainBody();
    this.emailSupportService.openInMailClient(this.to, subject, body);
    this.dialogRef.close(true);
  }

  toggleFormatToolbar(): void {
    this.showFormatToolbar = !this.showFormatToolbar;
  }

  toggleMenu(menu: OpenMenu, event: Event): void {
    event.stopPropagation();
    this.openMenu = this.openMenu === menu ? null : menu;
  }

  keepMenuOpen(event: Event): void {
    event.stopPropagation();
  }

  preventBlur(event: Event): void {
    event.preventDefault();
  }

  execCommand(command: string, value?: string): void {
    this.focusEditor();
    document.execCommand(command, false, value);
  }

  onFontChange(font: string): void {
    this.selectedFont = font;
    const fontMap: Record<string, string> = {
      'Sans Serif': 'Arial, sans-serif',
      Serif: 'Georgia, serif',
      'Fixed Width': 'Courier New, monospace',
      Wide: 'Arial Black, sans-serif',
      Narrow: 'Arial Narrow, sans-serif',
    };
    this.execCommand('fontName', fontMap[font] || font);
  }

  onFontSizeChange(label: string, value: string, event: Event): void {
    event.stopPropagation();
    this.selectedFontSize = label;
    this.openMenu = null;
    this.execCommand('fontSize', value);
  }

  onAlignChange(command: string, event: Event): void {
    event.stopPropagation();
    this.selectedAlign = command;
    this.openMenu = null;
    this.execCommand(command);
  }

  onSpacingChange(value: string, event: Event): void {
    event.stopPropagation();
    this.selectedSpacing = value;
    this.openMenu = null;
    this.focusEditor();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      this.bodyEditor.nativeElement.style.lineHeight = value;
      return;
    }
    const wrapper = document.createElement('div');
    wrapper.style.lineHeight = value;
    try {
      range.surroundContents(wrapper);
    } catch {
      this.bodyEditor.nativeElement.style.lineHeight = value;
    }
  }

  onTextColor(event: Event): void {
    const color = (event.target as HTMLInputElement).value;
    this.selectedTextColor = color;
    this.execCommand('foreColor', color);
  }

  insertLink(): void {
    this.openMenu = null;
    const url = window.prompt('Enter URL', 'https://');
    if (!url) {
      return;
    }
    this.focusEditor();
    const selection = window.getSelection();
    if (selection && selection.toString()) {
      this.execCommand('createLink', url);
      return;
    }
    this.execCommand('insertHTML', `<a href="${url}">${url}</a>`);
  }

  insertEmoji(emoji: string, event: Event): void {
    event.stopPropagation();
    this.openMenu = null;
    this.focusEditor();
    document.execCommand('insertText', false, emoji);
  }

  insertDriveLink(): void {
    this.openMenu = null;
    const url = window.prompt('Paste Google Drive link', 'https://drive.google.com/');
    if (!url) {
      return;
    }
    this.focusEditor();
    document.execCommand(
      'insertHTML',
      false,
      `<a href="${url}">${url}</a>`
    );
  }

  insertSignature(): void {
    this.openMenu = null;
    this.focusEditor();
    document.execCommand(
      'insertText',
      false,
      '\n\nBest regards,\n'
    );
  }

  toggleConfidential(): void {
    this.isConfidential = !this.isConfidential;
    this.openMenu = null;
  }

  enableConfidentialAndSend(): void {
    this.isConfidential = true;
    this.sendEmail();
  }

  closeMenus(): void {
    this.openMenu = null;
  }

  helpMeWrite(): void {
    this.openMenu = null;
    const text = window.prompt(
      'Describe what you need help writing',
      'I need help with...'
    );
    if (!text) {
      return;
    }
    this.focusEditor();
    document.execCommand('insertText', false, `\n${text}\n`);
  }

  selectAllBody(): void {
    this.openMenu = null;
    this.focusEditor();
    document.execCommand('selectAll');
  }

  triggerAttach(): void {
    this.openMenu = null;
    this.fileInput?.nativeElement?.click();
  }

  triggerImage(): void {
    this.openMenu = null;
    this.imageInput?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    this.attachmentNames.push(file.name);
    this.focusEditor();
    document.execCommand('insertText', false, `\n[Attachment: ${file.name}]\n`);
    input.value = '';
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.focusEditor();
      const src = reader.result as string;
      document.execCommand(
        'insertHTML',
        false,
        `<img src="${src}" alt="${file.name}" style="max-width:100%;height:auto;" />`
      );
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  private focusEditor(): void {
    this.bodyEditor?.nativeElement?.focus();
  }

  private buildPlainBody(): string {
    const editor = this.bodyEditor?.nativeElement;
    let body = editor?.innerText?.trim() || this.draft.body;
    if (this.attachmentNames.length) {
      body += `\n\nAttachments:\n${this.attachmentNames
        .map((name) => `- ${name}`)
        .join('\n')}`;
    }
    if (this.isConfidential) {
      body = `[Confidential]\n\n${body}`;
    }
    return body;
  }
}
